import { Injector, runInInjectionContext } from '@angular/core';
import { Observable, NEVER } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ForeignKeyConfig } from '../field-definition';

export interface FkResolverCallbacks {
  onResolved: () => void;
}

export function resolveDisplayFromItem(item: any, displayField: string): string | null {
  if (!item) {
    return null;
  }
  if (displayField.includes('.')) {
    const resolved = displayField.split('.').reduce((cur, part) => cur?.[part], item);
    return resolved !== null && resolved !== undefined ? String(resolved) : null;
  }
  const resolved = item?.[displayField];
  return resolved !== null && resolved !== undefined ? String(resolved) : null;
}

export interface FkDisplayResolver {
  resolve: (value: string | number) => string | null;
  cache: Map<string, string>;
  pending: Set<string>;
}

export function createFkDisplayResolver(
  injector: Injector,
  fkConfig: ForeignKeyConfig,
  callbacks: FkResolverCallbacks,
  destroy$?: Observable<void>
): FkDisplayResolver {
  const cache = new Map<string, string>();
  const pending = new Set<string>();
  const displayField = fkConfig.displayField || 'name';
  const keyField = fkConfig.keyField || 'id';
  const queuedValues = new Map<string, string | number>();
  let batchScheduled = false;

  const finalize = (valueStr: string, displayValue: string | null) => {
    const resolved = displayValue ?? valueStr;
    cache.set(valueStr, resolved);
    pending.delete(valueStr);
    callbacks.onResolved();
  };

  const hydrateFromSharedCache = (value: string | number): boolean => {
    const sharedItem = fkConfig.getCachedItem?.(value);
    if (!sharedItem) {
      return false;
    }

    cache.set(
      String(value),
      resolveDisplayFromItem(sharedItem, displayField) ?? String(value)
    );
    return true;
  };

  const flushQueuedValues = () => {
    if (!queuedValues.size) {
      return;
    }

    const getByIds = fkConfig.getByIds;
    if (!getByIds) {
      queuedValues.forEach((_, valueStr) => finalize(valueStr, null));
      queuedValues.clear();
      return;
    }

    const batch = Array.from(queuedValues.entries()).map(([valueStr, value]) => ({ valueStr, value }));
    queuedValues.clear();

    runInInjectionContext(injector, () =>
      getByIds(batch.map(item => item.value), { skipHandleError: true })
    ).pipe(
      take(1),
      takeUntil(destroy$ ?? NEVER)
    ).subscribe({
      next: (items: any[]) => {
        const itemMap = new Map(
          items.map((item: any) => [String(item?.[keyField]), item])
        );

        batch.forEach(({ valueStr }) => {
          finalize(valueStr, resolveDisplayFromItem(itemMap.get(valueStr), displayField));
        });
      },
      error: () => {
        batch.forEach(({ valueStr }) => finalize(valueStr, null));
      },
    });
  };

  const scheduleBatch = () => {
    if (batchScheduled) {
      return;
    }

    batchScheduled = true;
    queueMicrotask(() => {
      batchScheduled = false;
      flushQueuedValues();
    });
  };

  const resolve = (value: string | number): string | null => {
    const valueStr = String(value);
    const cachedValue = cache.get(valueStr);

    if (hydrateFromSharedCache(value)) {
      return cache.get(valueStr) ?? null;
    }

    if (cachedValue !== undefined) {
      return cachedValue;
    }

    if (pending.has(valueStr)) {
      return null;
    }

    const isNonNumericString =
      typeof value === 'string' && value.trim() !== '' && Number.isNaN(Number(value));

    if (isNonNumericString && keyField === 'id' && displayField === 'name') {
      cache.set(valueStr, value);
      return value;
    }

    pending.add(valueStr);
    queuedValues.set(valueStr, value);
    scheduleBatch();

    return null;
  };

  return { resolve, cache, pending };
}
